import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import humanChatService from '../../services/humanChatService';
import ConversationList from './ConversationList';
import ChatThread from './ChatThread';
import GuideSelector from './GuideSelector';
import '../../styles/group-chat-brutal.css';

/**
 * Main chat drawer component
 * Slides in from the right, contains conversations list and chat thread
 */
const GroupChatDrawer = ({
  isOpen,
  onClose,
  groupId,
  onUnreadChange
}) => {
  const { user } = useAuth();

  // View state: 'list' | 'chat' | 'new-chat' | 'chat-type-selector'
  const [view, setView] = useState('list');
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Refs to break infinite loops
  const hasCheckedGroupConvo = useRef(false);
  const conversationsRef = useRef(conversations);
  conversationsRef.current = conversations;

  // Load conversations
  const loadConversations = useCallback(async () => {
    if (!groupId) return;

    try {
      setLoading(true);
      setError(null);

      const data = await humanChatService.getConversations(groupId);

      if (data.success) {
        setConversations(data.conversations || []);

        // Calculate total unread and notify parent
        const totalUnread = (data.conversations || []).reduce(
          (sum, conv) => sum + (conv.unreadCount || 0),
          0
        );
        onUnreadChange?.(totalUnread);
      }
    } catch (err) {
      console.error('Failed to load conversations:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [groupId, onUnreadChange]);

  // Ensure group conversation exists (runs once per drawer open)
  const ensureGroupConversation = useCallback(async () => {
    if (!groupId || hasCheckedGroupConvo.current) return;
    hasCheckedGroupConvo.current = true;

    try {
      // Check if group conversation already exists using ref to avoid dep on conversations
      const groupConvo = conversationsRef.current.find(c => c.type === 'group');

      if (!groupConvo) {
        console.log('[GroupChat] No group conversation found, calling group-conversation endpoint');

        // Call the group-conversation endpoint which auto-adds user as participant
        await humanChatService.getGroupConversation(groupId);
        loadConversations(); // Refresh to show the group conversation
      }
    } catch (err) {
      console.error('[GroupChat] Error ensuring group conversation:', err);
    }
  }, [groupId, loadConversations]);

  // Load conversations on mount and when drawer opens
  useEffect(() => {
    if (isOpen && groupId) {
      loadConversations();
    }
  }, [isOpen, groupId, loadConversations]);

  // Poll for new conversations when drawer is open
  useEffect(() => {
    if (!isOpen || !groupId) return;

    const interval = setInterval(() => {
      if (!document.hidden) {
        loadConversations();
      }
    }, 30000); // Poll every 30 seconds

    return () => clearInterval(interval);
  }, [isOpen, groupId, loadConversations]);

  // Ensure group conversation exists when drawer opens
  useEffect(() => {
    if (isOpen && groupId) {
      ensureGroupConversation();
    }
  }, [isOpen, groupId, ensureGroupConversation]);

  // Handle conversation selection
  const handleSelectConversation = async (conversation) => {
    setSelectedConversation(conversation);
    setView('chat');

    // Mark as read
    if (conversation.unreadCount > 0) {
      try {
        await humanChatService.markAllRead(conversation._id);
        // Update local state
        setConversations(prev =>
          prev.map(c =>
            c._id === conversation._id ? { ...c, unreadCount: 0 } : c
          )
        );
        // Recalculate total unread
        const newTotalUnread = conversations.reduce(
          (sum, c) => sum + (c._id === conversation._id ? 0 : (c.unreadCount || 0)),
          0
        );
        onUnreadChange?.(newTotalUnread);
      } catch (err) {
        console.error('Failed to mark as read:', err);
      }
    }
  };

  // Handle back from chat to list
  const handleBackToList = () => {
    setView('list');
    setSelectedConversation(null);
    loadConversations(); // Refresh list
  };

  // Handle new chat button - show chat type selector
  const handleNewChat = () => {
    setView('chat-type-selector');
  };

  // Handle guide selection for new chat
  const handleGuideSelected = async (guide) => {
    try {
      const conversation = await humanChatService.findOrCreateConversation(
        groupId,
        guide.userId
      );
      setSelectedConversation(conversation);
      setView('chat');
      loadConversations(); // Refresh list
    } catch (err) {
      console.error('Failed to create conversation:', err);
      alert(err.message);
    }
  };

  // Handle message sent - update last message preview
  const handleMessageSent = (message) => {
    setConversations(prev =>
      prev.map(c =>
        c._id === selectedConversation?._id
          ? {
              ...c,
              lastMessage: {
                content: message.content,
                senderId: message.senderId._id || message.senderId,
                timestamp: message.createdAt
              },
              lastMessageAt: message.createdAt
            }
          : c
      )
    );
  };

  // Close handler with escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when drawer is open; reset group convo check on close
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      hasCheckedGroupConvo.current = false;
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Render drawer content based on view
  const renderContent = () => {
    if (loading && view === 'list') {
      return (
        <div className="chat-loading">
          <div className="chat-loading-spinner" />
          <span>LOADING CHATS...</span>
        </div>
      );
    }

    if (error && view === 'list') {
      const isApiNotAvailable = error.includes('not available');
      return (
        <div className="chat-error">
          <div className="chat-error-icon">{isApiNotAvailable ? '🚧' : '⚠️'}</div>
          <div className="chat-error-title">
            {isApiNotAvailable ? 'Coming Soon' : 'Failed to load'}
          </div>
          <div className="chat-error-text">
            {isApiNotAvailable
              ? 'Chat feature is being set up. Check back soon!'
              : error}
          </div>
          {!isApiNotAvailable && (
            <button className="chat-retry-btn" onClick={loadConversations}>
              Try Again
            </button>
          )}
        </div>
      );
    }

    switch (view) {
      case 'chat':
        return (
          <ChatThread
            conversation={selectedConversation}
            currentUserId={user?._id}
            onBack={handleBackToList}
            onMessageSent={handleMessageSent}
          />
        );

      case 'chat-type-selector':
        return (
          <div className="chat-type-selector">
            <div className="chat-type-header">
              <button className="chat-back-btn" onClick={handleBackToList}>
                ← Back
              </button>
              <h3>Start a Chat</h3>
            </div>

            <div className="chat-type-options">
              {/* Group Chat Option */}
              <button
                className="chat-type-option group"
                onClick={() => {
                  const groupConvo = conversations.find(c => c.type === 'group');
                  if (groupConvo) {
                    handleSelectConversation(groupConvo);
                  } else {
                    alert('Group chat is being set up. Please try again in a moment.');
                    loadConversations();
                  }
                }}
              >
                <div className="chat-type-icon">👥</div>
                <div className="chat-type-info">
                  <div className="chat-type-title">Message Entire Group</div>
                  <div className="chat-type-desc">
                    Send a message that all group members can see
                  </div>
                </div>
              </button>

              {/* Private Chat Option */}
              <button
                className="chat-type-option private"
                onClick={() => setView('new-chat')}
              >
                <div className="chat-type-icon">💬</div>
                <div className="chat-type-info">
                  <div className="chat-type-title">Private Message</div>
                  <div className="chat-type-desc">
                    Send a private message to a guide or teacher
                  </div>
                </div>
              </button>
            </div>
          </div>
        );

      case 'new-chat':
        return (
          <GuideSelector
            groupId={groupId}
            onSelectGuide={handleGuideSelected}
            onBack={() => setView('chat-type-selector')}
          />
        );

      case 'list':
      default:
        return (
          <ConversationList
            conversations={conversations}
            currentUserId={user?._id}
            onSelectConversation={handleSelectConversation}
            onNewChat={handleNewChat}
          />
        );
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`chat-drawer-backdrop ${isOpen ? 'open' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        className={`chat-drawer ${isOpen ? 'open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Group chat"
      >
        {/* Header */}
        <div className="chat-drawer-header">
          <h2 className="chat-drawer-title">
            {view === 'chat' && selectedConversation
              ? 'CHAT'
              : view === 'new-chat'
              ? 'NEW CHAT'
              : 'MESSAGES'}
          </h2>
          <button
            className="chat-drawer-close"
            onClick={onClose}
            aria-label="Close chat"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="chat-drawer-content">
          {renderContent()}
        </div>
      </div>
    </>
  );
};

export default GroupChatDrawer;
